import React, { useState, useEffect, useRef } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Bell, ChevronRight, User, LogOut, Package, ShoppingCart, Users, Loader2 } from 'lucide-react';
import api from '../../services/api';
import formatPrice from '../../utils/formatPrice';
import '../../css/adminheader.css';

/**
 * Shared Rubick-style top navbar with Global Search (Products, Orders, Customers)
 * and breadcrumb cleaner for Dashboard.
 */
const AdminHeader = ({ title, searchPlaceholder, onSearch }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Global search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState({ products: [], orders: [], customers: [] });
  const searchWrapperRef = useRef(null);

  const isDashboard = location.pathname === '/admin';

  const getRouteTitle = () => {
    if (title) return title;
    const path = location.pathname;
    if (path === '/admin') return 'Dashboard';
    if (path === '/admin/products') return 'Products';
    if (path.startsWith('/admin/products/new')) return 'Add Product';
    if (path.startsWith('/admin/products/textile')) return 'Textile Product';
    if (path.startsWith('/admin/products/')) return 'Edit Product';
    if (path.startsWith('/admin/orders')) return 'Orders';
    if (path.startsWith('/admin/users')) return 'Staff & Admins';
    if (path.startsWith('/admin/customer')) return 'Customers';
    if (path.startsWith('/admin/coupons')) return 'Coupons';
    if (path.startsWith('/admin/exclusive-banners')) return 'Exclusive Banners';
    if (path.startsWith('/admin/slider-banners')) return 'Slider Banners';
    if (path.startsWith('/admin/hero-banners')) return 'Hero Banners';
    if (path.startsWith('/admin/contact-us')) return 'Contact Inquiries';
    if (path.startsWith('/admin/courier-warehouse')) return 'Courier Warehouse';
    if (path.startsWith('/admin/catalog-health')) return 'Catalog Health';
    if (path.startsWith('/admin/size-charts') || path.startsWith('/admin/size_charts')) return 'Size Charts';
    if (path.startsWith('/admin/review-page')) return 'Reviews';
    if (path.startsWith('/admin/reels-page')) return 'Reels';
    if (path.startsWith('/admin/report/sales')) return 'Sales Report';
    if (path.startsWith('/admin/report/orders')) return 'Order Report';
    if (path.startsWith('/admin/report/customers')) return 'Customer Report';
    if (path.startsWith('/admin/report/products')) return 'Product Report';
    return 'Dashboard';
  };

  const displayTitle = getRouteTitle();

  // Debounced global search
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults({ products: [], orders: [], customers: [] });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const { data } = await api.get(`/admin/global-search?q=${encodeURIComponent(trimmed)}`);
        if (data) {
          setSearchResults(data);
          setSearchOpen(true);
        }
      } catch (err) {
        console.error('Error in global search:', err);
      } finally {
        setSearching(false);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside listener for profile and search dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (onSearch) {
      onSearch(trimmed);
      setSearchOpen(false);
      return;
    }
    if (trimmed) {
      setSearchOpen(false);
      if (trimmed.startsWith('#') || trimmed.toUpperCase().startsWith('ORD')) {
        navigate(`/admin/orders?search=${encodeURIComponent(trimmed)}`);
      } else if (trimmed.includes('@') || /^\d{10}$/.test(trimmed)) {
        navigate(`/admin/customer?search=${encodeURIComponent(trimmed)}`);
      } else {
        navigate(`/admin/products?search=${encodeURIComponent(trimmed)}`);
      }
    }
  };

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate('/auth');
  };

  const hasAnyResults =
    searchResults.products.length > 0 ||
    searchResults.orders.length > 0 ||
    searchResults.customers.length > 0;

  return (
    <header className="adh-navbar">
      <div className="adh-left">
        {/* Breadcrumb: Completely removed on Dashboard per user requirement */}
        {!isDashboard ? (
          <nav className="adh-breadcrumb" aria-label="Breadcrumb">
            <Link to="/admin" className="adh-crumb-link">Dashboard</Link>
            <ChevronRight size={14} className="adh-crumb-sep" />
            <span className="adh-crumb-current">{displayTitle}</span>
          </nav>
        ) : (
          <div className="adh-dashboard-brand">
            <span className="adh-dash-title">Mithralaya Admin</span>
          </div>
        )}
      </div>

      <div className="adh-right">
        {/* Global Search across Products, Orders, and Customers */}
        <div className="adh-search-wrapper" ref={searchWrapperRef}>
          <form onSubmit={handleSearchSubmit} className="adh-search">
            <Search size={15} className="adh-search-icon" />
            <input
              type="text"
              placeholder={searchPlaceholder || 'Search products, orders, customers...'}
              className="adh-search-input"
              value={searchQuery}
              onFocus={() => {
                if (searchQuery.trim().length >= 2) setSearchOpen(true);
              }}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searching && <Loader2 size={13} className="adh-search-spinner" />}
          </form>

          {/* Global Search Results Dropdown */}
          {searchOpen && searchQuery.trim().length >= 2 && (
            <div className="adh-global-dropdown">
              {hasAnyResults ? (
                <>
                  {/* 1. Products */}
                  {searchResults.products.length > 0 && (
                    <div className="adh-search-section">
                      <div className="adh-search-section-title">
                        <Package size={13} />
                        <span>Products ({searchResults.products.length})</span>
                      </div>
                      {searchResults.products.map((p) => (
                        <div
                          key={p._id}
                          className="adh-search-item"
                          onClick={() => {
                            setSearchOpen(false);
                            navigate(`/admin/products?search=${encodeURIComponent(p.name)}`);
                          }}
                        >
                          {p.image && <img src={mediaUrl(p.image)} alt={p.name} className="adh-item-thumb" />}
                          <div className="adh-item-info">
                            <span className="adh-item-name">{p.name}</span>
                            <span className="adh-item-sub">
                              {p.subCategory || p.category} {p.price ? `• ${formatPrice(p.price)}` : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 2. Orders */}
                  {searchResults.orders.length > 0 && (
                    <div className="adh-search-section">
                      <div className="adh-search-section-title">
                        <ShoppingCart size={13} />
                        <span>Orders ({searchResults.orders.length})</span>
                      </div>
                      {searchResults.orders.map((o) => (
                        <div
                          key={o._id}
                          className="adh-search-item"
                          onClick={() => {
                            setSearchOpen(false);
                            navigate(`/admin/orders?search=${encodeURIComponent(o.orderId || o._id)}`);
                          }}
                        >
                          <div className="adh-item-icon-chip">
                            <ShoppingCart size={13} />
                          </div>
                          <div className="adh-item-info">
                            <span className="adh-item-name">{o.orderId ? `#${o.orderId}` : 'Order'}</span>
                            <span className="adh-item-sub">
                              {formatPrice(o.totalPrice)} • {o.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 3. Customers */}
                  {searchResults.customers.length > 0 && (
                    <div className="adh-search-section">
                      <div className="adh-search-section-title">
                        <Users size={13} />
                        <span>Customers ({searchResults.customers.length})</span>
                      </div>
                      {searchResults.customers.map((c) => (
                        <div
                          key={c._id}
                          className="adh-search-item"
                          onClick={() => {
                            setSearchOpen(false);
                            navigate(`/admin/customer?search=${encodeURIComponent(c.name || c.email)}`);
                          }}
                        >
                          <div className="adh-item-icon-chip">
                            <User size={13} />
                          </div>
                          <div className="adh-item-info">
                            <span className="adh-item-name">{c.name || 'Customer'}</span>
                            <span className="adh-item-sub">{c.email || c.phone}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="adh-search-empty">
                  No matching products, orders, or customers found.
                </div>
              )}
            </div>
          )}
        </div>

        <button type="button" className="adh-icon-btn" title="Notifications">
          <Bell size={17} className="adh-bell-icon" />
          <span className="adh-bell-dot" />
        </button>

        {/* Top Right Profile Menu (Kept as single primary profile) */}
        <div className="adh-profile" ref={profileRef}>
          <button
            type="button"
            className="adh-profile-btn"
            onClick={() => setProfileOpen((o) => !o)}
          >
            <span className="adh-avatar">
              <User size={15} />
            </span>
            <span className="adh-profile-name">{user?.name || 'Akil'}</span>
            <span className={`adh-caret ${profileOpen ? 'open' : ''}`} />
          </button>

          {profileOpen && (
            <div className="adh-dropdown">
              <div className="adh-dropdown-user">
                <strong>{user?.name || 'Akil'}</strong>
                {user?.email && <span>{user.email}</span>}
              </div>
              <button type="button" className="adh-logout" onClick={handleLogout}>
                <LogOut size={14} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
